import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Typography,
  TypographyProps,
  Card,
  CardContent,
} from '@mui/material';
import moment from 'moment';

import { SubjectColors, TP_COLORS } from '../../../../constants';
import { IQuiz } from '../../../../store/QuizSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import { isMobileScreen, SafeAreaGaps } from '../../../../helper';
import { headerHeight } from '../../../../components/Mobile/Header';

export interface IPageHeader {
  boxProps?: BoxProps;
  quiz: IQuiz | undefined;
}

// STYLES
const titleTextProps: TypographyProps = {
  variant: 'h4',
  style: {
    color: TP_COLORS.white,
    fontWeight: 500,
  },
  sx: {
    fontSize: {
      xs: '18px',
      sm: '22px',
      md: '28px',
      lg: '30px',
      xl: '32px',
    },
  },
};
const customLightBlue = '#ccd7de';
const customBlue = '#4a718b';
// const subTitleTextProps: TypographyProps = {
//   variant: 'subtitle1',
//   style: {
//     color: TP_COLORS.white,
//     fontWeight: 500,
//     opacity: 0.6,
//   },
// };

// RENDER
const RENDER_COUNTDOWN_ITEM = (value: string, label: string) => (
  <div>
    <Card
      style={{
        width: '50px',
        height: '50px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: customLightBlue,
      }}
    >
      <Typography fontWeight={600} fontSize="25px" color={customBlue}>
        {value}
      </Typography>
    </Card>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      color={customBlue}
      textAlign="center"
      fontSize="12px"
    >
      {label}
    </Typography>
  </div>
);
const RENDER_COUNTDOWN_DIVIDER = () => (
  <div>
    <Card
      elevation={0}
      style={{
        width: '20px',
        height: '50px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography fontWeight={600} fontSize="25px" color={customBlue}>
        :
      </Typography>
    </Card>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      color={customBlue}
      textAlign="center"
    >
      &nbsp;
    </Typography>
  </div>
);

const PageHeader: React.FC = () => {
  // HOOKS
  const { user } = useAppSelector(getAuthState);

  // BOOLEANS
  const isMobile = isMobileScreen();
  const isInTrial =
    user &&
    user.quiztopiyaTrialExpiration &&
    user.assessmentEngineDate &&
    moment(user.quiztopiyaTrialExpiration).toDate().getTime() >=
      moment(user.assessmentEngineDate).toDate().getTime() &&
    moment(user.quiztopiyaTrialExpiration).toDate().getTime() >
      new Date().getTime();

  // LOCAL STATES
  const [totalSeconds, setTotalSeconds] = useState(0);
  // eslint-disable-next-line
  const [intervalId, setIntervalId] = useState<any>(undefined);

  // USE EFFECT
  useEffect(() => {
    if (isInTrial && user && user.quiztopiyaTrialExpiration) {
      const diff =
        moment(user.quiztopiyaTrialExpiration).toDate().getTime() -
        new Date().getTime();
      setTotalSeconds(diff / 1000);
      const interval = setInterval(() => {
        setTotalSeconds((prev) => prev - 1);
      }, 1000);
      setIntervalId(interval);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    if (totalSeconds <= 0) {
      clearInterval(intervalId);
    }
  }, [totalSeconds]);

  // COUNTDOWN
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds - days * 86400) / 3600);
  const minutes = Math.floor((totalSeconds - days * 86400 - hours * 3600) / 60);
  const seconds = Math.floor(
    totalSeconds - days * 86400 - hours * 3600 - minutes * 60
  );

  // STYLES
  const defaultContainerStyles: BoxProps['sx'] = {
    background: {
      xs: `url('${process.env.PUBLIC_URL}/images/shapes/quiztopiya-rocket.png') 60% bottom/30% no-repeat, url('${process.env.PUBLIC_URL}/images/shapes/cloud-top-right-corner.png') top right/contain no-repeat, url('${process.env.PUBLIC_URL}/images/shapes/cloud-green-white-layer.png') bottom center/60% no-repeat, ${SubjectColors[5]}`,
      lg: `url('${process.env.PUBLIC_URL}/images/shapes/quiztopiya-rocket.png') 60% center/12% no-repeat, url('${process.env.PUBLIC_URL}/images/shapes/cloud-top-right-corner.png') top right/contain no-repeat, url('${process.env.PUBLIC_URL}/images/shapes/cloud-green-white-layer.png') bottom center/60% no-repeat, ${SubjectColors[5]}`,
    },
    pt: {
      xs: `calc(${SafeAreaGaps.top} + ${headerHeight} + 15px)`,
      sm: `calc(${SafeAreaGaps.top} + 15px)`,
    },
    pb: '15px',
    pr: '15px',
    pl: {
      xs: '15px',
      sm: '20px',
      md: '30px',
      lg: '35px',
    },
    minHeight: '250px',
  };

  // RENDER
  return (
    <div>
      <Box component="div" sx={{ ...defaultContainerStyles }}>
        <div
          style={{
            paddingTop: isMobile
              ? `calc(15px + ${headerHeight} + ${SafeAreaGaps.top})`
              : '40px',
          }}
        >
          <Box
            sx={{
              display: { xs: 'inline', lg: 'flex' },
              justifyContent: { xs: 'normal', md: 'space-between' },
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: { xs: '15px', lg: '0' } }}>
              <Typography {...titleTextProps} variant="h1">
                Launchpad for Exam Success
              </Typography>
            </Box>
            {isInTrial && (
              <div
                style={
                  isMobile
                    ? { display: 'flex', justifyContent: 'flex-end' }
                    : undefined
                }
              >
                <Card>
                  <CardContent>
                    <Typography
                      fontWeight={600}
                      textAlign="center"
                      sx={{ mb: '15px' }}
                    >
                      {totalSeconds <= 0
                        ? 'Your trial quiz has been ended'
                        : 'Your trial quiz ends in'}
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {RENDER_COUNTDOWN_ITEM(
                        days.toString().padStart(2, '0'),
                        `Day${days > 1 ? 's' : ''}`
                      )}
                      {RENDER_COUNTDOWN_DIVIDER()}
                      {RENDER_COUNTDOWN_ITEM(
                        hours.toString().padStart(2, '0'),
                        `Hour${hours > 1 ? 's' : ''}`
                      )}
                      {RENDER_COUNTDOWN_DIVIDER()}
                      {RENDER_COUNTDOWN_ITEM(
                        minutes.toString().padStart(2, '0'),
                        `Minute${minutes > 1 ? 's' : ''}`
                      )}
                      {RENDER_COUNTDOWN_DIVIDER()}
                      {RENDER_COUNTDOWN_ITEM(
                        seconds.toString().padStart(2, '0'),
                        `Second${seconds > 1 ? 's' : ''}`
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </Box>
          {/* <Typography {...subTitleTextProps}>Sub Headig</Typography> */}
        </div>
      </Box>
    </div>
  );
};

export default PageHeader;
